'use client';

import { Avatar as AvatarPrimitive } from 'radix-ui';
import * as React from 'react';
import type { VariantProps } from 'tailwind-variants';

import { tv } from '../../lib/tv';

/**
 * The Figma Avatar and Avatar Group (page ↳ Avatar, 51:87 / 51:151): a
 * radius/full Base that clips its content, plus a Status dot that sits
 * bottom-right with an outside bg/surface stroke (1.5px at 20/24, 2px above).
 * Initials use a text style per size; the Icon fallback is Icon/user at ~55%
 * of the avatar in icon/secondary. Values read from the component sets'
 * bound variables.
 */
export const avatarVariants = tv({
  slots: {
    root: 'relative inline-flex shrink-0 align-middle select-none',
    base: 'relative flex size-full items-center justify-center overflow-hidden rounded-full',
    image: 'size-full object-cover',
    fallback: 'flex size-full items-center justify-center rounded-full',
    icon: 'shrink-0',
    status: 'absolute right-0 bottom-0 rounded-full',
  },
  variants: {
    size: {
      20: {
        root: 'size-5',
        fallback: 'text-avatar-20',
        icon: 'size-[11px]',
        status: 'size-1.5 ring-[1.5px]',
      },
      24: {
        root: 'size-6',
        fallback: 'text-avatar-24',
        icon: 'size-[13px]',
        status: 'size-2 ring-[1.5px]',
      },
      32: {
        root: 'size-8',
        fallback: 'text-label-sm',
        icon: 'size-[18px]',
        status: 'size-2.5 ring-2',
      },
      40: {
        root: 'size-10',
        fallback: 'text-body-md-strong',
        icon: 'size-[22px]',
        status: 'size-3 ring-2',
      },
      56: {
        root: 'size-14',
        fallback: 'text-avatar-56',
        icon: 'size-[31px]',
        status: 'size-3.5 ring-2',
      },
      80: {
        root: 'size-20',
        fallback: 'text-avatar-80',
        icon: 'size-11',
        status: 'size-[18px] ring-2',
      },
    },
    /** Figma "Type" of the fallback: Initials on Pale Sage, or the user Icon. */
    fallback: {
      initials: { fallback: 'bg-brand-subtle-hover text-text-brand' },
      icon: { fallback: 'bg-bg-muted text-icon-secondary' },
    },
    status: {
      online: { status: 'bg-status-success-solid ring-bg-surface' },
      away: { status: 'bg-status-warning-solid ring-bg-surface' },
      busy: { status: 'bg-status-danger-solid ring-bg-surface' },
    },
    /**
     * Inside an Avatar Group: a 2px inside bg/surface stroke separates the
     * overlaps. Drawn by an overlay so it also covers photos.
     */
    grouped: {
      true: {
        base: 'after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:inset-ring-2 after:inset-ring-bg-surface',
      },
    },
  },
  defaultVariants: { size: 32, fallback: 'icon' },
});

export type AvatarVariants = VariantProps<typeof avatarVariants>;

/** Avatar Group: overlap per size (-6 / -8 / -10), first avatar on top. */
export const avatarGroupVariants = tv({
  slots: {
    root: 'isolate inline-flex items-center',
    item: 'relative inline-flex',
    overflow:
      'bg-bg-emphasis text-text-secondary relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full inset-ring-2 inset-ring-bg-surface',
  },
  variants: {
    size: {
      24: { root: '-space-x-1.5', overflow: 'text-avatar-24 size-6' },
      32: { root: '-space-x-2', overflow: 'text-label-sm size-8' },
      40: { root: '-space-x-2.5', overflow: 'text-body-md-strong size-10' },
    },
  },
  defaultVariants: { size: 32 },
});

function Avatar({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return <AvatarPrimitive.Root data-slot="avatar" className={className} {...props} />;
}

function AvatarImage(props: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return <AvatarPrimitive.Image data-slot="avatar-image" {...props} />;
}

function AvatarFallback(props: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return <AvatarPrimitive.Fallback data-slot="avatar-fallback" {...props} />;
}

export { Avatar, AvatarFallback, AvatarImage };
